import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup, useSlotGroup, useProperty } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getContrastTextColor, contrastRatio, TEXT_ON_PRIMARY_DARK } from '../utils/luminance';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// "Tour the neighborhood" — BY DAY / BY NIGHT points-of-interest sliders
// ----------------------------------------------------------------------------
// A day/night toggle switches between two horizontal, scroll-snapping sliders
// of nearby points of interest. Rather than iterating the `neighborhood_pois`
// collection, each card is its own editable SLOT GROUP so an editor can
// hand-pick exactly which spots headline the daytime vs. nighttime experience
// (the POI data model has no time-of-day dimension, so the day/night split has
// to be authored). Each card's fields default to — and a generator preselects
// from — the neighborhood_pois collection.
//
// Theme-color handling (per design spec):
//   Brand-colored accents (eyebrow, card category label, arrow buttons) normally
//   use the property's primary color. But when the primary is light, its
//   auto-computed `textOnPrimary` is DARK (#1a1a1a) — a signal the primary won't
//   read well as text on the light section background — so we fall back to the
//   theme's near-black text instead of a washed-out primary. Everything that
//   varies with the palette reads from a single `--nt-accent` custom property.
// ============================================================================

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

function formatDistance(raw: string): string {
  const t = raw.trim();
  return /(mi|ft|km|min)$/i.test(t) || /min/i.test(t) ? t : `${t} mi`;
}

// ============================================================================
// Slot group definitions — fully-inlined literals so the preview system's
// static schema extractor can resolve them (no shared factory / referenced
// consts). Day cards default to POIs 0–3, night cards to POIs 4–7.
// ============================================================================

const dayPoi1Def: SlotGroupDefinition = {
  label: 'Daytime Spot 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[0].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[0].poi_name', defaultValue: 'Corner Coffee House' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[0].poi_type', defaultValue: 'Cafe' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[0].poi_miles_from_property', defaultValue: '0.3 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[0].poi_short_description', defaultValue: 'Start your morning with locally roasted espresso and fresh pastries just around the corner.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Daytime slider, card 1 of 4 — a lively daytime spot (cafe, coffee, breakfast). Each of the 4 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi2Def: SlotGroupDefinition = {
  label: 'Daytime Spot 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[1].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[1].poi_name', defaultValue: 'Midtown Market' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[1].poi_type', defaultValue: 'Shopping' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[1].poi_miles_from_property', defaultValue: '0.6 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[1].poi_short_description', defaultValue: 'Browse boutique shops and artisan goods along the neighborhood’s bustling main street.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 1,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Daytime slider, card 2 of 4 — a daytime shopping or retail destination. Each of the 4 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi3Def: SlotGroupDefinition = {
  label: 'Daytime Spot 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[2].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[2].poi_name', defaultValue: 'Riverside Park' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[2].poi_type', defaultValue: 'Park' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[2].poi_miles_from_property', defaultValue: '0.4 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[2].poi_short_description', defaultValue: 'Green lawns, walking trails and weekend markets make this the go-to daytime escape.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 2,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Daytime slider, card 3 of 4 — an outdoor / park / green space for the daytime. Each of the 4 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi4Def: SlotGroupDefinition = {
  label: 'Daytime Spot 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[3].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[3].poi_name', defaultValue: 'The Daily Kitchen' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[3].poi_type', defaultValue: 'Restaurant' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[3].poi_miles_from_property', defaultValue: '0.5 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[3].poi_short_description', defaultValue: 'A sunny brunch spot serving seasonal plates and fresh-pressed juices all afternoon.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 3,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Daytime slider, card 4 of 4 — a daytime dining / brunch spot. Each of the 4 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi1Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[4].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[4].poi_name', defaultValue: 'Lumen Cocktail Bar' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[4].poi_type', defaultValue: 'Nightlife' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[4].poi_miles_from_property', defaultValue: '0.4 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[4].poi_short_description', defaultValue: 'Craft cocktails and a low-lit ambiance for an intimate evening out.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 4,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Nighttime slider, card 1 of 4 — a bar / cocktail / nightlife spot. Each of the 4 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi2Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[5].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[5].poi_name', defaultValue: 'The Velvet Room' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[5].poi_type', defaultValue: 'Live Music' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[5].poi_miles_from_property', defaultValue: '0.7 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[5].poi_short_description', defaultValue: 'Catch local bands and DJs in this beloved after-dark music venue.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 5,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Nighttime slider, card 2 of 4 — a live music / entertainment venue. Each of the 4 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi3Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[6].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[6].poi_name', defaultValue: 'Ember Steakhouse' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[6].poi_type', defaultValue: 'Restaurant' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[6].poi_miles_from_property', defaultValue: '0.5 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[6].poi_short_description', defaultValue: 'Candlelit dinners and a curated wine list make for the perfect night out.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 6,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Nighttime slider, card 3 of 4 — an evening dinner / fine-dining spot. Each of the 4 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi4Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[7].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[7].poi_name', defaultValue: 'Skyline Lounge' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[7].poi_type', defaultValue: 'Rooftop Bar' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[7].poi_miles_from_property', defaultValue: '0.8 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[7].poi_short_description', defaultValue: 'Sip and unwind with panoramic city views from this rooftop hotspot.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 7,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Tour the Neighborhood slider.',
    context: 'Nighttime slider, card 4 of 4 — a rooftop / lounge / late-night hangout. Each of the 4 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

type TabKey = 'day' | 'night';

// ============================================================================
// Presentational card (no hooks — lives inside each EditableSlotGroup)
// ============================================================================

interface CardValues {
  image?: string | null;
  name?: string | null;
  category?: string | null;
  distance?: string | null;
  description?: string | null;
}

function PoiCard({ image, name, category, distance, description }: CardValues) {
  return (
    <>
      <div className="nt-card-media">
        {hasVal(image) ? (
          <VibesitesImage
            url={image}
            alt={name || ''}
            sizes="(min-width: 640px) 300px, 74vw"
            className="nt-card-img"
          />
        ) : (
          <div className="nt-card-img nt-card-img--empty" />
        )}
        {hasVal(distance) && <span className="nt-card-distance">{formatDistance(distance)}</span>}
      </div>
      <div className="nt-card-body">
        {hasVal(category) && <p className="nt-card-type">{category}</p>}
        {hasVal(name) && <h3 className="nt-card-name">{name}</h3>}
        {hasVal(description) && <p className="nt-card-desc">{description}</p>}
      </div>
    </>
  );
}

export function NeighborhoodTour() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const property = useProperty();

  // ── Theme-aware accent color ────────────────────────────────────────────
  // Light primary → dark textOnPrimary → primary washes out on the light
  // background, so use the theme's near-black text instead. Otherwise primary.
  const primaryColor = (property?.property_primary_color as string) || '#4a7c59';
  const accentColor = useMemo(() => {
    const onPrimaryIsDark = getContrastTextColor(primaryColor) === TEXT_ON_PRIMARY_DARK;
    return onPrimaryIsDark ? 'var(--vibe-color-text)' : primaryColor;
  }, [primaryColor]);

  // Section heading color: primary when it reads well, black when the primary is
  // a light tone (dark textOnPrimary) so the title never washes out.
  const headingColor = useMemo(() => {
    const onPrimaryIsDark = getContrastTextColor(primaryColor) === TEXT_ON_PRIMARY_DARK;
    return onPrimaryIsDark ? '#000000' : primaryColor;
  }, [primaryColor]);

  // ── Card text color, resolved against the *actual* card surface ──────────
  // Cards sit on --vibe-color-surface, which a property sets (property_color_3)
  // independently of --vibe-color-text (property_font_color). Nothing guarantees
  // those two contrast: a dark surface paired with dark theme text leaves the POI
  // name + description nearly unreadable. So we read the resolved surface at
  // runtime and, when the theme text doesn't clear a readable ratio against it,
  // swap in a contrasting tone (white on dark, near-black on light). Null =
  // theme text is fine, so the CSS falls back to --vibe-color-text.
  const [cardText, setCardText] = useState<{ base: string; muted: string } | null>(null);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const surface = cs.getPropertyValue('--vibe-color-surface').trim();
    const text = cs.getPropertyValue('--vibe-color-text').trim();
    if (!surface) return;
    if (contrastRatio(surface, text) >= 4.5) {
      setCardText(null);
      return;
    }
    const base = getContrastTextColor(surface); // '#ffffff' | '#1a1a1a'
    // Use the full contrasting tone (e.g. solid white on a dark surface) for the
    // description too — a faded/muted grey read as washed-out low-contrast copy.
    setCardText({ base, muted: base });
  }, [property?.property_color_3, property?.property_font_color]);

  // Resolve each card's editable/generated values.
  const day1 = useSlotGroup('neighborhoodtour.day_poi_1', dayPoi1Def);
  const day2 = useSlotGroup('neighborhoodtour.day_poi_2', dayPoi2Def);
  const day3 = useSlotGroup('neighborhoodtour.day_poi_3', dayPoi3Def);
  const day4 = useSlotGroup('neighborhoodtour.day_poi_4', dayPoi4Def);
  const night1 = useSlotGroup('neighborhoodtour.night_poi_1', nightPoi1Def);
  const night2 = useSlotGroup('neighborhoodtour.night_poi_2', nightPoi2Def);
  const night3 = useSlotGroup('neighborhoodtour.night_poi_3', nightPoi3Def);
  const night4 = useSlotGroup('neighborhoodtour.night_poi_4', nightPoi4Def);

  const [activeTab, setActiveTab] = useState<TabKey>('day');

  // ── Slider scroll state ─────────────────────────────────────────────────
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= max - 2);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Reset to the start whenever the active slider changes.
    el.scrollLeft = 0;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, activeTab]);

  const scrollByCard = useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.nt-card');
    const step = card ? card.offsetWidth + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up nt-section"
      style={{
        '--nt-accent': accentColor,
        ...(cardText ? { '--nt-card-text': cardText.base, '--nt-card-desc': cardText.muted } : {}),
      } as CSSProperties}
    >
      <div className="nt-inner">
        <div className="nt-head">
          <RichTextContent
            path="neighborhoodtour.eyebrow"
            defaultValue="<p>Explore the area</p>"
            as="p"
            className="nt-eyebrow"
          />
          <RichTextContent
            path="neighborhoodtour.section_heading"
            defaultValue="<p>Tour the neighborhood.</p>"
            as="h2"
            className="nt-heading"
            style={{ color: headingColor }}
          />
          <RichTextContent
            path="neighborhoodtour.body"
            defaultField="property_short_description"
            defaultValue="<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>"
            as="div"
            className="nt-body"
          />
        </div>

        <div className="nt-tabs" role="tablist" aria-label="Explore the neighborhood by time of day">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'day'}
            className={`nt-tab${activeTab === 'day' ? ' nt-tab--active' : ''}`}
            onClick={() => setActiveTab('day')}
          >
            By Day
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'night'}
            className={`nt-tab${activeTab === 'night' ? ' nt-tab--active' : ''}`}
            onClick={() => setActiveTab('night')}
          >
            By Night
          </button>
        </div>

        <div className="nt-slider" role="tabpanel">
          {activeTab === 'day' ? (
            <div ref={trackRef} className="nt-track" key="day">
              <EditableSlotGroup groupPath="neighborhoodtour.day_poi_1" definition={dayPoi1Def} className="nt-card">
                <PoiCard {...day1} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.day_poi_2" definition={dayPoi2Def} className="nt-card">
                <PoiCard {...day2} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.day_poi_3" definition={dayPoi3Def} className="nt-card">
                <PoiCard {...day3} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.day_poi_4" definition={dayPoi4Def} className="nt-card">
                <PoiCard {...day4} />
              </EditableSlotGroup>
            </div>
          ) : (
            <div ref={trackRef} className="nt-track" key="night">
              <EditableSlotGroup groupPath="neighborhoodtour.night_poi_1" definition={nightPoi1Def} className="nt-card">
                <PoiCard {...night1} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.night_poi_2" definition={nightPoi2Def} className="nt-card">
                <PoiCard {...night2} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.night_poi_3" definition={nightPoi3Def} className="nt-card">
                <PoiCard {...night3} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodtour.night_poi_4" definition={nightPoi4Def} className="nt-card">
                <PoiCard {...night4} />
              </EditableSlotGroup>
            </div>
          )}

          <div className="nt-controls">
            <button
              type="button"
              className="nt-arrow"
              onClick={() => scrollByCard(-1)}
              disabled={atStart}
              aria-label="Previous points of interest"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 3.5L5.5 9l5.5 5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="nt-arrow"
              onClick={() => scrollByCard(1)}
              disabled={atEnd}
              aria-label="Next points of interest"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 3.5L12.5 9L7 14.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .nt-section {
          /* A subtle tint (theme text mixed into the page background) sets the
             section apart from the surrounding page so it reads as its own band.
             Works in both light and dark themes since it derives from the theme
             tokens rather than a fixed color. */
          background: color-mix(in srgb, var(--vibe-color-text) 5%, var(--vibe-color-background));
          padding: 3rem 0;
        }
        @media (min-width: 768px) {
          .nt-section { padding: 4.5rem 0; }
        }
        .nt-inner {
          max-width: 78rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .nt-inner { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .nt-inner { padding: 0 2rem; } }

        .nt-head {
          max-width: 44rem;
          margin: 0 auto 2rem;
          text-align: center;
        }
        .nt-eyebrow {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          /* Primary color, or near-black when the primary would wash out. */
          color: var(--nt-accent);
          margin: 0 0 0.75rem;
        }
        .nt-eyebrow p { margin: 0; }
        .nt-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.9rem, 3.4vw, 2.75rem);
          font-weight: 600;
          line-height: 1.1;
          color: var(--vibe-color-text);
          margin: 0 0 1rem;
        }
        .nt-body {
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          font-weight: 400;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .nt-body p { margin: 0; }

        /* ── Day / Night toggle ── */
        .nt-tabs {
          display: flex;
          justify-content: center;
          gap: 0;
          margin: 0 auto 2.25rem;
        }
        .nt-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          padding: 0.7rem 1.6rem;
          border: 1px solid var(--vibe-color-border);
          background: transparent;
          color: var(--vibe-color-text-muted);
          cursor: pointer;
          transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;
        }
        .nt-tab:first-child { border-radius: 3px 0 0 3px; }
        .nt-tab:last-child { border-radius: 0 3px 3px 0; border-left: none; }
        .nt-tab:hover { color: var(--vibe-color-text); }
        .nt-tab--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }
        /* On the selected tab the base :hover would swap in the dark/grey body
           text color, which reads poorly on the primary background. Keep the
           on-primary text and only dim it slightly (a touch of the primary
           bleeds through) so the hover stays high-contrast. */
        .nt-tab--active:hover {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 82%, var(--vibe-color-primary));
        }

        /* ── Slider ── */
        .nt-slider { position: relative; }
        .nt-track {
          display: flex;
          gap: 1.25rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          padding: 0.5rem 1rem 1.25rem;
          margin: 0 -1rem;
          scroll-padding-left: 1rem;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: var(--vibe-color-border) transparent;
          animation: nt-fade 0.35s ease both;
        }
        @media (min-width: 640px) {
          .nt-track { margin: 0 -1.5rem; padding-left: 1.5rem; padding-right: 1.5rem; scroll-padding-left: 1.5rem; }
        }
        @media (min-width: 1024px) {
          .nt-track { margin: 0 -2rem; padding-left: 2rem; padding-right: 2rem; scroll-padding-left: 2rem; }
        }
        .nt-track::-webkit-scrollbar { height: 6px; }
        .nt-track::-webkit-scrollbar-track { background: transparent; }
        .nt-track::-webkit-scrollbar-thumb { background: var(--vibe-color-border); border-radius: 3px; }

        /* ── Card (the EditableSlotGroup element) ── */
        .nt-card {
          scroll-snap-align: start;
          flex: 0 0 74%;
          max-width: 74%;
          background: var(--vibe-color-surface);
          border: 1px solid var(--vibe-color-border);
          /* Squared-off corners for a sharper, more architectural look. */
          border-radius: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        @media (min-width: 640px) { .nt-card { flex-basis: 300px; max-width: 300px; } }

        .nt-card-media {
          position: relative;
          width: 100%;
          /* Portrait crop makes each tile taller and more vertical. */
          aspect-ratio: 3 / 4;
          background: var(--vibe-color-border);
        }
        .nt-card-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .nt-card-img--empty { background: var(--vibe-color-border); }
        .nt-card-distance {
          position: absolute;
          left: 0.75rem;
          bottom: 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          padding: 0.3rem 0.7rem;
          border-radius: 0;
          background: rgba(0, 0, 0, 0.62);
          color: #ffffff;
          backdrop-filter: blur(2px);
        }

        .nt-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 1.15rem 1.25rem 1.35rem;
        }
        .nt-card-type {
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--nt-accent);
          margin: 0;
        }
        .nt-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          line-height: 1.2;
          /* --nt-card-text is set only when the theme text wouldn't read on the
             card surface (e.g. a dark surface); otherwise fall back to it. */
          color: var(--nt-card-text, var(--vibe-color-text));
          margin: 0;
        }
        .nt-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          /* Full theme text color (no fade) so the copy renders at the same
             strength as the card name rather than a washed-out grey. Visual
             hierarchy is carried by the name's larger size and heavier weight.
             --nt-card-desc is swapped in when the surface needs a contrasting
             tone (dark theme text on a dark card). */
          font-weight: 400;
          line-height: 1.6;
          color: var(--nt-card-desc, var(--vibe-color-text));
          margin: 0.15rem 0 0;
        }

        /* ── Arrow controls ── */
        .nt-controls {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }
        .nt-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 9999px;
          border: none;
          background: var(--nt-accent);
          color: #ffffff;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .nt-arrow:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px color-mix(in srgb, var(--nt-accent) 32%, transparent);
        }
        .nt-arrow:active:not(:disabled) { transform: translateY(0); }
        .nt-arrow:disabled { opacity: 0.28; cursor: default; }
        .nt-arrow svg { display: block; }

        @keyframes nt-fade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nt-track { animation: none; scroll-behavior: auto; }
          .nt-arrow { transition: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodTour',
  displayName: 'Tour the Neighborhood',
  tokens: {
    'neighborhoodtour.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Explore the area</p>',
    },
    'neighborhoodtour.section_heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Tour the neighborhood.</p>',
    },
    'neighborhoodtour.body': {
      label: 'Intro Text',
      type: 'richtext',
      defaultField: 'property_short_description',
      defaultValue:
        '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'neighborhoodtour.day_poi_1': dayPoi1Def,
    'neighborhoodtour.day_poi_2': dayPoi2Def,
    'neighborhoodtour.day_poi_3': dayPoi3Def,
    'neighborhoodtour.day_poi_4': dayPoi4Def,
    'neighborhoodtour.night_poi_1': nightPoi1Def,
    'neighborhoodtour.night_poi_2': nightPoi2Def,
    'neighborhoodtour.night_poi_3': nightPoi3Def,
    'neighborhoodtour.night_poi_4': nightPoi4Def,
  },
};

(NeighborhoodTour as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodTour;
